import React, { useEffect, useState } from "react";
import { ChevronRight, KeyRound, PenTool, Plus, Trash2, UserPen, Users, X } from "lucide-react";
import { practiceApi } from "../lib/practiceApi";
import { SignatureUpload } from "./SignatureUpload";
import { Switch } from "./ui/switch";

const EMPTY = { last_name: "", first_name: "", email: "", role: "collaborator", password: "" };
const fullName = (last, first) => [String(last || "").trim(), String(first || "").trim()].filter(Boolean).join(" ");

export const TeamManager = ({ currentUserId }) => {
  const [team, setTeam] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [reset, setReset] = useState(null);
  const [rename, setRename] = useState(null);
  const [stamp, setStamp] = useState(null);
  const [invite, setInvite] = useState(true);
  const [flash, setFlash] = useState("");
  const [error, setError] = useState("");
  const [permissionBusy, setPermissionBusy] = useState("");

  async function changeEconomics(member, value) {
    setPermissionBusy(member.id); setError("");
    try {
      const result = await practiceApi(`/users/${member.id}/permissions/economics`, {
        method: "PATCH", body: JSON.stringify({ can_view_economics: value }) });
      setTeam(previous => previous.map(row => row.id === member.id ? { ...row, ...result } : row));
      setFlash(`${member.name}: situazione economica ${value ? "visibile" : "nascosta"}.`);
    } catch (err) { setError(err.message); }
    finally { setPermissionBusy(""); }
  }

  const load = () => practiceApi("/users").then(setTeam).catch(err => setError(err.message));
  useEffect(() => { load(); }, []);

  async function submit(event) {
    event.preventDefault(); setError("");
    try {
      if (invite) {
        const result = await practiceApi("/users/invite", { method: "POST", body: JSON.stringify({ name: fullName(form.last_name, form.first_name), email: form.email, role: form.role }) });
        setFlash(result.email_sent
          ? `Invito inviato a ${result.email}: potrà entrare con Google o impostare la password dal collegamento`
          : `Utente creato, ma l'email a ${result.email} non è stata recapitata: comunica tu l'accesso`);
      } else {
        await practiceApi("/users", { method: "POST", body: JSON.stringify({ name: fullName(form.last_name, form.first_name), email: form.email, role: form.role, password: form.password || "Studio2026!" }) });
        setFlash("");
      }
      setForm(EMPTY); setOpen(false); load();
    } catch (err) { setError(err.message); }
  }

  async function submitReset(event) {
    event.preventDefault(); setError("");
    if ((reset.password || "").length < 8) { setError("La password deve contenere almeno 8 caratteri"); return; }
    try {
      await practiceApi(`/users/${reset.id}/password`, { method: "POST", body: JSON.stringify({ new_password: reset.password }) });
      setFlash(`Password di ${reset.name} reimpostata. Comunicagliela per il prossimo accesso.`);
      setTimeout(() => setFlash(""), 6000);
      setReset(null);
    } catch (err) { setError(err.message); }
  }

  async function submitRename(event) {
    event.preventDefault(); setError("");
    try {
      await practiceApi(`/users/${rename.id}/profile`, { method: "PUT", body: JSON.stringify({ name: fullName(rename.last_name, rename.first_name), first_name: rename.first_name }) });
      setFlash(`Nome aggiornato: nel saluto comparirà "${rename.first_name || rename.name}".`);
      setTimeout(() => setFlash(""), 6000);
      setRename(null); load();
    } catch (err) { setError(err.message); }
  }

  async function remove(id) {
    if (!window.confirm("Rimuovere questo membro dallo studio?")) return;
    try { await practiceApi(`/users/${id}`, { method: "DELETE" }); load(); }
    catch (err) { setError(err.message); }
  }

  return <div data-testid="team-manager">
    <div className="settings-team-head">
      <span>{team.length} {team.length === 1 ? "membro dello studio" : "membri dello studio"}</span>
      <button className="primary" data-testid="add-user-button" id="team-invite-button" onClick={() => { setFlash(""); setOpen(true); }}><Plus size={15} />Invita collaboratore</button>
    </div>
    {flash && <div className="flash" data-testid="team-flash">{flash}</div>}
    {error && <div className="error" role="alert" data-testid="team-error">{error}</div>}
    <div className="data-table">
      <div className="table-head" style={{ gridTemplateColumns: "1.5fr 2fr 1fr 1fr 150px" }}>
        <span>nome</span><span>email</span><span>ruolo</span><span>riepilogo dashboard</span><span>azioni</span>
      </div>
      {team.length ? team.map((member, index) => <div className="table-row" key={member.id} data-testid={`team-row-${index}`} style={{ gridTemplateColumns: "1.5fr 2fr 1fr 1fr 150px" }}>
        <span>{member.name}{member.first_name ? <em className="settings-you"> · {member.first_name}</em> : null}{member.id === currentUserId && <em className="settings-you"> (tu)</em>}</span>
        <span>{member.email}</span>
        <span><span className={`status ${member.role === "admin" ? "good" : "pending"}`}>{member.role === "admin" ? "Admin" : "Collaboratore"}</span></span>
        <span style={{ fontSize: 11, color: "var(--muted)" }}>
          {member.role === "admin" ? <span data-testid={`team-economics-admin-${member.id}`}>Economia sempre visibile</span> :
            <label style={{ display: "flex", alignItems: "center", gap: 8, textTransform: "none", letterSpacing: 0 }}>
              <Switch data-testid={`team-economics-switch-${member.id}`} checked={member.can_view_economics === true}
                disabled={!!permissionBusy} aria-label={`Può vedere la situazione economica: ${member.name}`}
                onCheckedChange={value => changeEconomics(member, value)} />
              <span>Può vedere la situazione economica</span>
            </label>}
        </span>
        <span style={{ display: "flex", gap: 6 }}>          <button className="icon-btn" title="Correggi nome" data-testid={`rename-user-${index}`} onClick={() => { setError(""); setRename({ id: member.id, name: member.name || "", last_name: member.last_name || (member.name || "").split(" ").slice(member.first_name ? 1 : 0).join(" ") || member.name || "", first_name: member.first_name || "" }); }}><UserPen size={14} /></button>
          <button className="icon-btn" title="Reimposta password" data-testid={`reset-password-${index}`} onClick={() => { setError(""); setReset({ id: member.id, name: member.name, password: "" }); }}><KeyRound size={14} /></button>
          <button className="icon-btn" title="Timbro e firma" data-testid={`member-stamp-${index}`} onClick={() => { setError(""); setStamp({ id: member.id, name: member.name }); }}><PenTool size={14} /></button>
          {member.role !== "admin" && <button className="icon-btn" title="Rimuovi membro" data-testid={`delete-user-${index}`} onClick={() => remove(member.id)}><Trash2 size={14} /></button>}
        </span>
      </div>) : <div className="empty"><Users size={28} /><strong>Nessun collaboratore</strong><span>Aggiungi un membro per delegare i lavori.</span></div>}
    </div>

    {open && <div className="modal-backdrop">
      <form className="create-modal" onSubmit={submit}>
        <button type="button" className="modal-close" data-testid="close-user-modal" onClick={() => setOpen(false)}><X /></button>
        <p className="eyebrow">NUOVO ACCESSO</p>
        <h2>{invite ? "Invita nel team" : "Aggiungi al team"}</h2>
        <label>Cognome<input data-testid="user-last-name-input" required value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} /></label>
        <label>Nome<input data-testid="user-name-input" required value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} /></label>
        <p className="muted" style={{ fontSize: 11.5, margin: "0 0 6px" }}>Verrà salvato come <b>{fullName(form.last_name, form.first_name) || "Cognome Nome"}</b>: nello studio le persone si scrivono sempre prima con il cognome.</p>
        <label>Email<input data-testid="user-email-input" type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></label>
        <label>Ruolo<select data-testid="user-role-select" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
          <option value="collaborator">Collaboratore</option>
          <option value="admin">Amministratore</option>
        </select></label>
        <label>Password iniziale<input data-testid="user-password-input" type="text" placeholder="Studio2026!" disabled={invite} value={invite ? "" : form.password} onChange={e => setForm({ ...form, password: e.target.value })} /></label>
        <label className="quote-switch" data-testid="user-invite-switch">Invia email di invito (entra con Google o imposta la password)
          <input type="checkbox" data-testid="user-invite-toggle" checked={invite} onChange={e => setInvite(e.target.checked)} /></label>
        {error && <div className="error" data-testid="user-form-error">{error}</div>}
        <button className="primary wide" data-testid="user-submit-button">{invite ? "Invia invito" : "Crea accesso"} <ChevronRight size={17} /></button>
      </form>
    </div>}

    {reset && <div className="modal-backdrop">
      <form className="create-modal" onSubmit={submitReset}>
        <button type="button" className="modal-close" data-testid="close-reset-modal" onClick={() => setReset(null)}><X /></button>
        <p className="eyebrow">REIMPOSTA PASSWORD</p>
        <h2>{reset.name}</h2>
        <p className="muted" style={{ fontSize: 12, margin: "0 0 6px" }}>Imposta una nuova password e comunicala all'utente: le sue sessioni aperte verranno disconnesse.</p>
        <label>Nuova password<input data-testid="reset-password-input" type="text" required value={reset.password} onChange={e => setReset({ ...reset, password: e.target.value })} placeholder="minimo 8 caratteri" /></label>
        {error && <div className="error" data-testid="reset-password-error">{error}</div>}
        <button className="primary wide" data-testid="reset-password-submit">Reimposta password <ChevronRight size={17} /></button>
      </form>
    </div>}
    {rename && <div className="modal-backdrop">
      <form className="create-modal" onSubmit={submitRename}>
        <button type="button" className="modal-close" data-testid="close-rename-modal" onClick={() => setRename(null)}><X /></button>
        <p className="eyebrow">NOME DEL MEMBRO</p>
        <h2>{rename.name}</h2>
        <p className="muted" style={{ fontSize: 12, margin: "0 0 6px" }}>Le persone si scrivono sempre <b>Cognome Nome</b> (es. Di Gesù Gianluca): il nome di battesimo viene usato nei saluti della dashboard.</p>
        <label>Cognome<input data-testid="rename-last-name" required value={rename.last_name} placeholder="Di Gesù" onChange={e => setRename({ ...rename, last_name: e.target.value })} /></label>
        <label>Nome<input data-testid="rename-first-name" value={rename.first_name} placeholder="Gianluca" onChange={e => setRename({ ...rename, first_name: e.target.value })} /></label>
        <p className="muted" style={{ fontSize: 11.5, margin: "0 0 6px" }}>Verrà salvato come <b>{fullName(rename.last_name, rename.first_name) || "Cognome Nome"}</b>.</p>
        {error && <div className="error" data-testid="rename-error">{error}</div>}
        <button className="primary wide" data-testid="rename-submit">Salva nome <ChevronRight size={17} /></button>
      </form>
    </div>}
    {stamp && <div className="modal-backdrop">
      <div className="create-modal" data-testid="member-stamp-modal">
        <button type="button" className="modal-close" data-testid="close-stamp-modal" onClick={() => setStamp(null)}><X /></button>
        <p className="eyebrow">TIMBRO E FIRMA</p>
        <h2>{stamp.name}</h2>
        <p className="muted" style={{ fontSize: 12, margin: "0 0 10px" }}>Carica il timbro personale del tecnico: verrà usato nei verbali e nelle lettere dei cantieri di cui è responsabile.</p>
        <SignatureUpload userId={stamp.id} name={stamp.name} testid="member-stamp" />
      </div>
    </div>}
  </div>;
};
